import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { DocumentsService } from './documentsApi';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import { IDocumentsListState } from '../../../../interfaces/documents/IDocumentsListState';
import { AxiosError } from 'axios';

const initialState: IDocumentsListState = {
    documentsAreLoading: false,
    documentIsLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchQuestionsDocuments = createAsyncThunk<any, IPaginationPayload>(
    'documents/tryToFetchQuestionsDocuments',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await DocumentsService.tryToFetchQuestionsDocuments(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result;
    },
);

export const tryToFetchAllQuestionsDocuments = createAsyncThunk<any, any>(
    'documents/tryToFetchAllQuestionsDocuments',
    async (data: any) => {
        const result = await DocumentsService.tryToFetchAllQuestionsDocuments(data);
        return result?.data;
    }
);

export const tryToFetchQuestionsDocumentsStats = createAsyncThunk<any, undefined>(
    'documents/tryToFetchQuestionsDocumentsStats',
    async () => {
        const result = await DocumentsService.tryToFetchQuestionsDocumentsStats();
        return result?.data;
    }
);

export const tryToFetchSourceConfusions = createAsyncThunk<any, IPaginationPayload>(
    'documents/tryToFetchSourceConfusions',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await DocumentsService.tryToFetchSourceConfusions(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result;
    },
);

export const tryToCreateReportIssue = createAsyncThunk<any, any>(
    'documents/tryToCreateReportIssue',
    async (data, { rejectWithValue }) => {
        try {
            const result =
                await DocumentsService.tryToCreateReportIssue(data);
            return result.data?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToFetchAllSourceConfusions = createAsyncThunk<any, any>(
    'documents/tryToFetchAllSourceConfusions',
    async (data: any) => {
        const result = await DocumentsService.tryToFetchAllSourceConfusions(data);
        return result?.data;
    }
);

export const tryToFetchSourceConfusionsStats = createAsyncThunk<any, undefined>(
    'documents/tryToFetchSourceConfusionsStats',
    async () => {
        const result = await DocumentsService.tryToFetchSourceConfusionsStats();
        return result?.data;
    }
);

export const tryToFetchSentimentQuestions = createAsyncThunk<any, IPaginationPayload>(
    'documents/tryToFetchSentimentQuestions',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await DocumentsService.tryToFetchSentimentQuestions(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result;
    },
);

export const tryToFetchAllSentimentQuestions = createAsyncThunk<any, any>(
    'documents/tryToFetchAllSentimentQuestions',
    async (data: any) => {
        const result = await DocumentsService.tryToFetchAllSentimentQuestions(data);
        return result?.data;
    }
);

export const tryToFetchSentimentQuestionsStats = createAsyncThunk<any, undefined>(
    'documents/tryToFetchSentimentQuestionsStats',
    async () => {
        const result = await DocumentsService.tryToFetchSentimentQuestionsStats();
        return result?.data;
    }
);

export const tryToFetchAllStaffUsageData = createAsyncThunk<any, any | undefined>(
    'documents/tryToFetchAllStaffUsageData',
    async () => {
        const result = await DocumentsService.tryToFetchAllStaffUsageData();
        return result?.data?.data;
    }
);

export const tryToFetchStaffUsageStats = createAsyncThunk<any, undefined>(
    'documents/tryToFetchStaffUsageStats',
    async () => {
        const result = await DocumentsService.tryToFetchStaffUsageStats();
        return result?.data?.data;
    }
);

export const documentsSlice = createSlice({
    name: 'documents',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchQuestionsDocuments
            .addCase(tryToFetchQuestionsDocuments.pending, (state) => {
                state.documentsAreLoading = true;
            })
            .addCase(tryToFetchQuestionsDocuments.rejected, (state) => {
                state.documentsAreLoading = false;
            })
            .addCase(tryToFetchQuestionsDocuments.fulfilled, (state, action) => {
                state.documentsAreLoading = false;
                state.questions = action.payload;
            })

            // tryToFetchSourceConfusions
            .addCase(tryToFetchSourceConfusions.pending, (state) => {
                state.documentsAreLoading = true;
            })
            .addCase(tryToFetchSourceConfusions.rejected, (state) => {
                state.documentsAreLoading = false;
            })
            .addCase(tryToFetchSourceConfusions.fulfilled, (state, action) => {
                state.documentsAreLoading = false;
                state.confusion = action.payload;
            })

            // tryToFetchSentimentQuestions
            .addCase(tryToFetchSentimentQuestions.pending, (state) => {
                state.documentsAreLoading = true;
            })
            .addCase(tryToFetchSentimentQuestions.rejected, (state) => {
                state.documentsAreLoading = false;
            })
            .addCase(tryToFetchSentimentQuestions.fulfilled, (state, action) => {
                state.documentsAreLoading = false;
                state.sentiments = action.payload;
            })
    },
});

export default documentsSlice.reducer;